import {PlusIcon, SpaceIcon, StarFillIcon} from '@primer/octicons-react'
import {Button, IconButton, Link, SelectPanel, type SelectPanelItemInput as ItemInput} from '@primer/react'
import {useEffect, useState, useRef, useCallback, useMemo} from 'react'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useDebounce} from '@github-ui/use-debounce'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import styles from './SpacesSelectionMenu.module.css'
import type {Repository} from '@github-ui/current-repository'
import {SpacesSelectionCreateSpaceDialog as CreateSpaceDialog} from './SpacesSelectionCreateSpaceDialog'
import type {FileReference, IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {useUpsertCopilotSpace} from '../hooks/use-upsert-copilot-space-no-chat-state'
import type {CopilotSpaceGitHubFileResource} from '../types'
import {languageMap} from '@github-ui/copilot-chat/utils/language-info'
import {getCopilotSpacePath} from '@github-ui/copilot-chat/utils/custom-copilots-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {announce} from '@github-ui/aria-live'

export function SpacesSelectionMenu({
  repository,
  currentFile,
  onBannerChange,
}: {
  repository: Repository
  currentFile: FileReference
  onBannerChange?: (bannerProps: BannerProps | null) => void
}) {
  const [open, setOpen] = useState(false)
  const isUpsertingRef = useRef(false)
  const triggerButtonRef = useRef<HTMLButtonElement>(null)

  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  const [loading, setLoading] = useState(false)

  const [filteredItems, setFilteredItems] = useState<IndexCopilotSpace[]>([])
  const [initialSpaces, setInitialSpaces] = useState<IndexCopilotSpace[]>([])
  const [initialSpacesLoaded, setInitialSpacesLoaded] = useState(false)
  const [showCreateSpaceDialog, setShowCreateSpaceDialog] = useState(false)
  const [selectedSpace, setSelectedSpace] = useState<IndexCopilotSpace | undefined>(undefined)
  const [hasError, setHasError] = useState(false)

  const {upsertCopilotSpace} = useUpsertCopilotSpace(selectedSpace)

  const isSupportedFile = useMemo(() => {
    const fileName = currentFile.path.split('/').pop() || ''
    const lastDotIndex = fileName.lastIndexOf('.')
    const fileExtension = lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1) : ''
    if (fileExtension === '') return true // Files without an extension will be considered supported
    return Object.values(languageMap).some(lang => lang.extensions?.includes(`.${fileExtension}`))
  }, [currentFile.path])

  useEffect(() => {
    if (!isUpsertingRef.current || !selectedSpace) return

    const fileResource: CopilotSpaceGitHubFileResource = {
      id: '',
      repositoryId: currentFile.repoID,
      nwo: `${currentFile.repoOwner}/${currentFile.repoName}`,
      filePath: currentFile.path,
      sha: currentFile.commitOID,
      fileExists: true,
      type: 'github_file',
      markedForDestroy: false,
    }

    const performUpsert = async () => {
      try {
        await upsertCopilotSpace({
          resources: [fileResource],
        })
        setHasError(false)
        setOpen(false)
        if (onBannerChange && selectedSpace) {
          const spacePath = getCopilotSpacePath({id: selectedSpace.id, owner: selectedSpace.owner})
          onBannerChange({
            variant: 'success' as const,
            onDismiss: () => onBannerChange(null),
            title: 'none',
            description: (
              <>
                File added to{' '}
                <Link href={spacePath} inline>
                  {selectedSpace.name}
                </Link>{' '}
                successfully
              </>
            ),
            hideTitle: true,
            style: {marginBottom: '16px'},
          })
        }
        setFilter('')
        setDebouncedFilter('')
        // Announce the error after a short delay to ensure the banner is rendered
        // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
        setTimeout(() => announce(`File added to ${selectedSpace.name} successfully.`), 250)
      } catch {
        setHasError(true)
        if (onBannerChange) {
          onBannerChange({
            variant: 'critical' as const,
            onDismiss: () => onBannerChange(null),
            title: 'none',
            description: 'There was an error adding the file to the space.',
            hideTitle: true,
            style: {marginBottom: '16px'},
          })
          // Announce the error after a short delay to ensure the banner is rendered
          // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
          setTimeout(() => announce(`There was an error adding the file to the space`), 250)
        }
      } finally {
        isUpsertingRef.current = false
      }
    }

    performUpsert()
  }, [selectedSpace, upsertCopilotSpace, currentFile, onBannerChange])

  const fetchResults = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        q: debouncedFilter,
        owner: repository.private ? repository.ownerLogin : '',
        editable_only: 'true',
      })

      const res = await verifiedFetchJSON(`/github-copilot/chat/custom_copilots/search?${params.toString()}`)

      if (!res.ok) throw new Error('Failed to fetch spaces')
      const data = (await res.json()) as IndexCopilotSpace[]
      return data
    } catch {
      return []
    }
  }, [debouncedFilter, repository.private, repository.ownerLogin])

  const handleSetFilteredItems = useCallback(() => {
    const lastSelectedSpace = copilotLocalStorage.getLastSelectedSpace()
    let newSpacesList = initialSpaces
    if (lastSelectedSpace) {
      // Reset the last select space for cross org private repositories
      if (lastSelectedSpace.ownerIsOrg && lastSelectedSpace.owner !== repository.ownerLogin && repository.private) {
        copilotLocalStorage.setLastSelectedSpace(undefined)
        setFilteredItems(initialSpaces)
        return
      }
      // Return all spaces except the last selected space if it exists in the list
      newSpacesList = newSpacesList.filter(s => !(s.id === lastSelectedSpace.id && s.owner === lastSelectedSpace.owner))
      if (newSpacesList.length === initialSpaces.length) {
        newSpacesList.pop() // Remove the last item to maintain the same length
      }
      newSpacesList = [lastSelectedSpace, ...newSpacesList]
    }
    setFilteredItems(newSpacesList)
  }, [initialSpaces, repository.ownerLogin, repository.private])

  useEffect(() => {
    let cancelled = false
    const fetchAndSetSpaces = async () => {
      if (open === false) return
      setLoading(true)
      if (debouncedFilter === '' && initialSpacesLoaded) {
        if (!cancelled) {
          handleSetFilteredItems()
          setLoading(false)
        }
        return
      }
      const spaces = await fetchResults()
      if (!cancelled) {
        setFilteredItems(spaces)
        if (!initialSpacesLoaded) {
          setInitialSpaces(spaces)
          setInitialSpacesLoaded(true)
        }
        setLoading(false)
      }
    }
    fetchAndSetSpaces()
    return () => {
      cancelled = true
    }
  }, [fetchResults, debouncedFilter, initialSpacesLoaded, open, handleSetFilteredItems])

  const items: ItemInput[] = filteredItems.map(space => ({
    id: `${space.owner}-${space.id}`,
    key: `${space.owner}-${space.id}`,
    text: space.name,
    description: space.owner,
    descriptionVariant: 'inline',
    value: `${space.owner}-${space.id}`,
    trailingVisual: space.starred ? () => <StarFillIcon className={styles.starButton} /> : undefined,
  }))

  const onSelectedChange = (selectedItem: ItemInput | undefined) => {
    setHasError(false)
    const newSelectedSpace = filteredItems.find(space => `${space.owner}-${space.id}` === selectedItem?.id)
    setSelectedSpace(newSelectedSpace)
    copilotLocalStorage.setLastSelectedSpace(newSelectedSpace)
    isUpsertingRef.current = true
    sendEvent('copilot_spaces.code_view', {
      target: 'COPILOT_SPACES_CODE_VIEW_MENU_SPACE_SELECTED',
    })
  }

  const handleCreateSpace = () => {
    setShowCreateSpaceDialog(true)
    sendEvent('copilot_spaces.code_view', {
      target: 'COPILOT_SPACES_CODE_VIEW_MENU_CREATE_SPACE_CLICKED',
    })
    setOpen(false)
  }

  const handleMenuOpen = (value: boolean) => {
    // Don't allow closing if there's an error state or if an upsert is pending
    if (!value && (hasError || isUpsertingRef.current)) {
      return
    }

    setOpen(value)

    if (value) {
      sendEvent('copilot_spaces.code_view', {target: 'COPILOT_SPACES_CODE_VIEW_MENU_OPENED'})
      setHasError(false)
    } else {
      setFilter('')
      setDebouncedFilter('')
    }
  }

  const selected: ItemInput | undefined = undefined

  const singleSelectProps = {
    selected,
    onSelectedChange: (selectedItem: ItemInput | undefined) => onSelectedChange(selectedItem),
  }

  const updateDebouncedFilter = useDebounce(setDebouncedFilter, 350, {onChangeBehavior: 'cancel'})

  if (!isSupportedFile) {
    return null
  }

  return (
    <>
      <SelectPanel
        title="Select a space"
        onCancel={() => setOpen(false)}
        anchorRef={triggerButtonRef}
        renderAnchor={anchorProps => (
          <IconButton icon={SpaceIcon} size="small" aria-label="Add to space" tooltipDirection="n" {...anchorProps} />
        )}
        open={open}
        onOpenChange={handleMenuOpen}
        loading={loading}
        items={items}
        filterValue={filter}
        onFilterChange={(value: string) => {
          setFilter(value)
          updateDebouncedFilter(value)
        }}
        {...singleSelectProps}
        overlayProps={{
          height: 'auto',
          width: 'large',
        }}
        message={
          filteredItems.length === 0 && !loading
            ? {
                title: 'No spaces found',
                body: 'You can create a new space to get started.',
                variant: 'empty',
                action: (
                  <Button variant="default" size="small" leadingVisual={PlusIcon} onClick={handleCreateSpace}>
                    Create a new space
                  </Button>
                ),
              }
            : undefined
        }
        secondaryAction={
          filteredItems.length > 0 ? (
            <SelectPanel.SecondaryActionButton onClick={handleCreateSpace}>
              Create a space
            </SelectPanel.SecondaryActionButton>
          ) : undefined
        }
      />
      {showCreateSpaceDialog && (
        <CreateSpaceDialog
          closeDialog={() => setShowCreateSpaceDialog(false)}
          currentFile={currentFile}
          returnFocusRef={triggerButtonRef}
        />
      )}
    </>
  )
}

export type BannerProps = {
  variant: 'success' | 'critical'
  onDismiss: () => void
  title: 'none'
  description: React.ReactNode
  hideTitle: boolean
  style: {marginBottom: string}
}
